import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { logEvent } from "firebase/analytics";
import { analytics } from '../../firebase';
import logo from '../../assets/logo.png';
import tictactoeImage from '../../assets/apps/tictactoe-infinite.png';
import dutyvigilImage from '../../assets/apps/dutyvigil/screenshot.png';
import teamlinkImage from '../../assets/apps/teamlink/screenshot.png';
import playBadge from '../../assets/get-It-on-google-play.svg';
import chromeIcon from '../../assets/chrome-logo.png';

interface AppConfig {
    name: string;
    displayName?: string;
    description: string;
    image: string;
    logo?: string;
    link: string;
    playStoreLink: string;
    primaryColor: string;
    accentColor: string;
    type?: 'mobile' | 'desktop' | 'web';
    tags?: string[];
    rating?: string;
    downloads?: string;
}

const apps: AppConfig[] = [
    {
        name: 'DutyVigil',
        description: 'Fatigue-aware duty allocation and monitoring system for mission-critical operations.',
        image: dutyvigilImage,
        logo: '/apps/dutyvigil/logo.png',
        link: '/dutyvigil',
        playStoreLink: 'mailto:support@tattvadynamics.com',
        primaryColor: '#5B7742',
        accentColor: '#bf7039',
        type: 'desktop',
        tags: ['Enterprise', 'Desktop'],
        rating: '5.0',
        downloads: 'Enterprise'
    },
    {
        name: 'TeamLink',
        description: 'Bookmark manager with augmented search, team collaboration, and onboarding features. Build your knowledge bank.',
        image: teamlinkImage,
        logo: '/apps/teamlink/logo.png',
        link: '/teamlink',
        playStoreLink: 'https://chromewebstore.google.com/detail/teamlink/keefgegllkbafoneklpidccocjllbenl',
        primaryColor: '#2563eb',
        accentColor: '#bf7039',
        type: 'web',
        tags: ['Productivity', 'Collaboration'],
        rating: '5.0',
        downloads: '10K+'
    },
    {
        name: 'Tic Tac Toe Infinite',
        description: 'Challenge AI or play with friends & family. No draws!',
        image: tictactoeImage,
        logo: '/apps/tictactoe-infinite/logo.png',
        link: '/tictactoe',
        playStoreLink: 'https://play.google.com/store/apps/details?id=com.tattvadynamics.tictactoe_infinite',
        primaryColor: '#481565',
        accentColor: '#bf7039',
        type: 'mobile',
        tags: ['Strategy', 'AI', '2 Player'],
        rating: '4.8',
        downloads: '1K+'
    },
    {
        name: 'Credstone',
        displayName: 'c r e d s t o n e',
        description: 'Understand and track credit card milestones, offers, and more. Maximize your rewards.',
        image: '/apps/credstone/logo.png',
        logo: '/apps/credstone/logo.png',
        link: '/credstone',
        playStoreLink: 'https://play.google.com/store/apps/details?id=com.tattvadynamics.credstone',
        primaryColor: '#ffffff',
        accentColor: '#bf7039',
        type: 'mobile',
        tags: ['Credit Cards', 'Milestones'],
        rating: '5.0',
        downloads: '100+'
    }
];

export default function SimpleLanding({ filterApp }: { filterApp?: string }) {
  const filteredApps = filterApp 
    ? apps.filter(app => app.name.toLowerCase().includes(filterApp.toLowerCase()))
    : apps;

  const [currentSlide, setCurrentSlide] = useState(0);
  const appsPerView = 1;
  const totalSlides = Math.ceil(filteredApps.length / appsPerView);

  const activeApp = filteredApps[currentSlide];
  const brandColor = '#bf7039';
  const activePrimaryColor = filterApp ? (activeApp?.primaryColor || brandColor) : brandColor;
  const headerLogo = filterApp && filteredApps[0]?.logo ? filteredApps[0].logo : logo;
  const headerName = filterApp && filteredApps[0] ? (filteredApps[0].displayName || filteredApps[0].name) : 'Tattva Dynamics';

  useEffect(() => {
    if (totalSlides <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 8000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  const handleAppClick = (appName: string) => {
    logEvent(analytics, 'app_click', {
      app_name: appName,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="min-h-screen bg-black overflow-hidden flex flex-col">
      <div className="flex items-center justify-between p-4 sm:p-5">
        <div className="flex items-center min-w-0">
          <img className="h-8 sm:h-10 w-auto flex-shrink-0" src={headerLogo} alt={headerName} />
          <span className={`ml-3 sm:ml-4 text-lg sm:text-xl font-semibold transition-colors duration-1000 truncate ${!filterApp ? 'text-primary-500' : ''}`} style={filterApp ? { color: activePrimaryColor } : {}} title={headerName}>{headerName}</span>
        </div>
        <nav className="flex items-center gap-6 pr-2">
           <a href="/blog" className="text-xs font-bold uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">Blog</a>
           <a href="mailto:hello@tattvadynamics.com" className="text-xs font-bold uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">Contact</a>
        </nav>
      </div>

      <div className="flex-grow flex flex-col justify-center py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 mb-12">
          <div className="mx-auto max-w-2xl text-center">
            <motion.h1
              key={`title-${activeApp?.name}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white"
            >
              {filterApp ? (filteredApps[0]?.displayName || filteredApps[0]?.name) : (
                <>Essence. <span className="transition-colors duration-1000 text-primary-500">Engineered.</span></>
              )}
            </motion.h1>
            <motion.p
              key={`desc-${activeApp?.name}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-3 sm:mt-4 text-base sm:text-lg leading-7 text-gray-300"
            >
              {filterApp ? filteredApps[0]?.description : 'Crafting solutions grounded in the essence of clarity and purpose.'}
            </motion.p>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 pb-8 lg:px-8">
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-1000 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                  <div key={slideIndex} className="w-full flex-shrink-0 flex justify-center px-4">
                    {filteredApps.slice(slideIndex * appsPerView, (slideIndex + 1) * appsPerView).map((app) => (
                      <motion.a
                        key={app.name}
                        href={app.playStoreLink}
                        target={app.type === 'desktop' ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => handleAppClick(app.name)}
                        className="w-[320px] sm:w-[360px] md:w-[400px] rounded-xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                      >
                        {app.type === 'desktop' || app.type === 'web' ? (
                          <div className="relative h-[320px] sm:h-[360px] md:h-[400px] flex flex-col gap-1 bg-gray-900/40">
                            <div className="flex-grow w-full flex items-center justify-center p-4 overflow-hidden">
                              <img
                                src={app.image}
                                alt={app.name}
                                className={`w-full h-full object-contain object-center drop-shadow-2xl ${app.type === 'desktop' ? 'scale-[2.4]' : 'scale-[1.1]'}`}
                              />
                            </div>

                            <div className="p-5 flex flex-col gap-3 bg-black">
                              <div className="flex justify-between items-center min-w-0">
                                <h3 className={`text-lg sm:text-xl font-bold truncate flex-grow pr-4 ${!filterApp ? 'text-primary-500' : ''}`} style={filterApp ? { color: app.primaryColor } : {}} title={app.displayName || app.name}>{app.displayName || app.name}</h3>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {app.tags?.map((tag, i) => {
                                        const isMainTag = i === 0;
                                        const tagColor = isMainTag ? (filterApp ? app.primaryColor : brandColor) : '#9ca3af';
                                        return (
                                            <span key={tag} className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border" style={{ 
                                                color: tagColor,
                                                backgroundColor: isMainTag ? `${tagColor}1a` : 'rgba(255,255,255,0.05)',
                                                borderColor: isMainTag ? `${tagColor}33` : 'rgba(255,255,255,0.1)'
                                            }}>
                                                {tag}
                                            </span>
                                        );
                                    })}
                                </div>
                              </div>
                              
                              <p className="text-xs sm:text-sm text-gray-300 line-clamp-2 leading-relaxed opacity-90">
                                {app.description}
                              </p>

                              {(app.rating || app.downloads) && (
                                <div className="flex items-center gap-3">
                                  {app.rating && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-yellow-400 text-[10px]">★</span>
                                      <span className="text-[10px] font-medium text-gray-300">{app.rating}</span>
                                    </div>
                                  )}
                                  {app.rating && app.downloads && <div className="h-2.5 w-[1px] bg-gray-700"></div>}
                                  {app.downloads && (
                                    <span className="text-[10px] text-gray-400 font-medium">{app.downloads}{app.downloads.includes('+') ? ' Downloads' : ''}</span>
                                  )}
                                </div>
                              )}
                              
                              <div className="flex justify-end pr-[10px]">
                                {app.type === 'desktop' ? (
                                  <span className="flex items-center justify-center gap-2 h-10 sm:h-11 w-28 sm:w-32 rounded-lg bg-black border border-white/50 text-white shadow-lg shadow-black/50 hover:scale-105 transition-all px-3">
                                      <img src="/apps/dutyvigil/logo.png" alt="" className="h-6 w-6 object-contain flex-shrink-0" />
                                      <span className="text-sm sm:text-base font-[550] leading-none text-center">Enquire</span>
                                  </span>
                                ) : (
                                  <div className="flex items-center justify-center gap-3 h-12 sm:h-14 w-auto min-w-[150px] sm:min-w-[170px] px-4 sm:px-5 rounded-lg bg-black border border-white/50 text-white shadow-lg shadow-black/50 hover:brightness-125 hover:scale-[1.03] active:scale-95 transition-all duration-200 cursor-pointer">
                                      <div className="flex-shrink-0">
                                          <img src={chromeIcon} alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                                      </div>
                                      <div className="flex flex-col items-start leading-[1.1]">
                                          <span className="text-[8px] sm:text-[9px] font-normal text-gray-400">Available in the</span>
                                          <span className="text-[11px] sm:text-[13px] font-semibold whitespace-nowrap">Chrome web store</span>
                                      </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="relative h-[320px] sm:h-[360px] md:h-[400px] flex bg-black">
                            <div className="w-[45%] flex items-center justify-center p-4 pr-0 overflow-hidden">
                              <img
                                src={app.image}
                                alt={app.name}
                                className="w-full h-full object-contain object-center drop-shadow-2xl scale-[1.05]"
                              />
                            </div>
                            <div className="w-[55%] bg-gradient-to-r from-black/90 to-black/70 p-4 pl-2 flex flex-col justify-end min-w-0">
                              <h3 className={`text-base sm:text-lg font-medium truncate ${!filterApp ? 'text-primary-500' : ''}`} style={filterApp ? { color: app.primaryColor } : {}} title={app.displayName || app.name}>{app.displayName || app.name}</h3>
                              <p className="mt-1 text-sm text-gray-300 line-clamp-3">{app.description}</p>
                              <div className="mt-2 flex items-center gap-2">
                                  {app.tags?.map((tag, i) => {
                                      const isMainTag = i === 0;
                                      const tagColor = isMainTag ? (filterApp ? app.primaryColor : brandColor) : '#9ca3af';
                                      return (
                                          <span key={tag} className="flex items-center gap-2">
                                              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: tagColor }}>{tag}</span>
                                              {i < (app.tags?.length || 0) - 1 && <span className="text-gray-600">•</span>}
                                          </span>
                                      );
                                  })}
                              </div>
                              
                              <div className="mt-2.5 flex items-center gap-3">
                                  {app.rating && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-yellow-400 text-xs">★</span>
                                      <span className="text-[11px] font-medium text-gray-300">{app.rating}</span>
                                    </div>
                                  )}
                                  {app.rating && app.downloads && <div className="h-3 w-[1px] bg-gray-700"></div>}
                                  {app.downloads && (
                                    <span className="text-[11px] text-gray-400 font-medium">{app.downloads}{app.downloads.includes('+') ? ' Downloads' : ''}</span>
                                  )}
                              </div>

                              <div className="mt-4 mb-1 flex justify-center">
                                  <div className="h-11 sm:h-13 w-36 sm:w-40 overflow-hidden relative shadow-lg shadow-black/50 rounded-lg bg-black hover:brightness-125 hover:scale-[1.03] active:scale-95 transition-all duration-200 cursor-pointer">
                                      <img 
                                          src={playBadge} 
                                          alt="Get it on Google Play" 
                                          className="w-full h-full object-contain"
                                      />
                                  </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </motion.a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center mt-10 space-x-3">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index);
                    logEvent(analytics, 'slider_navigation', {
                      slide_index: index,
                      total_slides: totalSlides
                    });
                  }}
                  className={`w-12 h-1.5 rounded-full transition-all duration-500 ${
                    currentSlide === index ? 'w-16' : 'bg-white/10 hover:bg-white/20'
                  }`}
                  style={{ 
                    backgroundColor: currentSlide === index ? activePrimaryColor : undefined 
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <footer className="p-8 mt-auto border-t border-white/5 opacity-40 hover:opacity-100 transition-opacity">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-[10px] uppercase tracking-[0.5em] text-white">© 2026 Tattva Dynamics. All rights reserved.</p>
          <div className="flex gap-8 text-[10px] font-bold uppercase tracking-widest text-white/60">
            <a href="/blog" className="hover:text-white transition-colors">Blog</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/legal" className="hover:text-white transition-colors">Legal</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
